import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
    Copy, 
    Check, 
    Diamond, 
    Upload, 
    CheckCircle2, 
    User, 
    Mail, 
    Phone, 
    MapPin, 
    Shield, 
    Crown, 
    Camera, 
    Package, 
    Briefcase, 
    LogOut,
    Sparkles,
    Save,
    AlertCircle,
    Ticket
} from 'lucide-react';
import { updateAuthProfile, uploadAuthProfileImage, getPromotions } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { getTranslation as t, isRTL } from '../utils/translations';

const ProfileCompletionPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated, loading, setProfile, logout } = useAuth();
    const isRtl = isRTL();

    const [fullName, setFullName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [copied, setCopied] = useState(false);
    const [voucherCopied, setVoucherCopied] = useState(null);
    const [availablePromotions, setAvailablePromotions] = useState([]);
    const [loadingPromotions, setLoadingPromotions] = useState(true);

    useEffect(() => {
        let isMounted = true;
        getPromotions()
            .then((promos) => {
                if (isMounted) {
                    setAvailablePromotions(Array.isArray(promos) ? promos : []);
                    setLoadingPromotions(false);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setAvailablePromotions([]);
                    setLoadingPromotions(false);
                }
            });
        return () => { isMounted = false; };
    }, []);

    // Eligible coupons for current user (strictly from verified backend promotions)
    const userEligibleCoupons = useMemo(() => {
        return availablePromotions.map(p => ({
            id: `promo_${p.id || p.code}`,
            code: p.code,
            titleAr: p.description || p.code,
            titleEn: p.description || p.code,
            descAr: p.minOrderAmount ? `الحد الأدنى للطلب $${p.minOrderAmount}` : 'عرض ترويجي ساري على المشتريات المؤهلة',
            descEn: p.minOrderAmount ? `Minimum order: $${p.minOrderAmount}` : 'Valid promotion on eligible orders',
            amount: p.discountType === 'PERCENTAGE' ? `${p.discountValue}%` : `$${p.discountValue}`,
            badge: p.discountType === 'PERCENTAGE' ? 'PERCENT OFF' : 'FIXED SAVINGS',
            discountType: p.discountType,
            discountValue: p.discountValue,
            minOrderAmount: p.minOrderAmount
        }));
    }, [availablePromotions]);

    const handleCopyId = () => {
        if (user?.customerId) {
            navigator.clipboard.writeText(user.customerId);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        }
    };

    const returnPath = useMemo(() => {
        const requestedPath = location.state?.from;
        return typeof requestedPath === 'string' && requestedPath.startsWith('/') ? requestedPath : '/';
    }, [location.state?.from]);

    useEffect(() => {
        if (loading) return;
        if (!isAuthenticated) {
            navigate('/login', { replace: true });
            return;
        }

        const initialFullName = user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
        setFullName(initialFullName);
        setPhoneNumber(user?.phoneNumber || '');
        setAddress(user?.addressLine || '');
        setCity(user?.city || '');
        setCountry(user?.country || '');
    }, [isAuthenticated, loading, navigate, user]);

    const handleImageChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setSubmitting(true);
        setError('');
        setSuccessMsg('');
        try {
            const profile = await uploadAuthProfileImage(file);
            setProfile(profile);
            setSuccessMsg(isRtl ? 'تم تحديث الصورة الشخصية بنجاح! ✨' : 'Profile photo updated successfully! ✨');
            setTimeout(() => setSuccessMsg(''), 4000);
        } catch (err) {
            setError(err?.response?.data?.message || (isRtl ? 'فشل رفع الصورة' : 'Failed to upload image'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        setError('');
        setSuccessMsg('');
        try {
            const profile = await updateAuthProfile({
                fullName,
                phoneNumber,
                address,
                city,
                country
            });
            setProfile(profile);
            setSuccessMsg(isRtl ? '🎉 تم حفظ وتحديث بيانات حسابك بنجاح!' : '🎉 Account details updated successfully!');
            setTimeout(() => {
                if (location.state?.from) {
                    navigate(returnPath, { replace: true });
                } else {
                    setSuccessMsg('');
                }
            }, 2500);
        } catch (err) {
            const data = err?.response?.data;
            if (data?.data && typeof data.data === 'object') {
                const messages = Object.values(data.data).join('\n• ');
                setError(`${isRtl ? 'أخطاء التحقق:' : 'Validation Errors:'}\n• ${messages}`);
            } else {
                setError(data?.message || (isRtl ? 'تعذر حفظ البيانات، يرجى المحاولة لاحقاً.' : 'Unable to update profile. Please try again.'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="luxury-account-page-wrapper">
            <div className="container luxury-account-container">
                {/* Breadcrumbs */}
                <div className="luxury-account-breadcrumbs">
                    <Link to="/">{t('HOME') || 'الرئيسية'}</Link>
                    <span className="breadcrumb-separator">/</span>
                    <span>{t('ACCOUNT_SETTINGS') || 'الملف الشخصي والحساب'}</span>
                </div>

                {/* Executive Hero Banner */}
                <div className="luxury-account-header">
                    <div className="account-header-glow" />
                    <div className="account-header-main">
                        <div className="account-avatar-wrapper">
                            <div className="account-avatar-ring">
                                {user?.profileImageUrl ? (
                                    <img src={user.profileImageUrl} alt="Profile" className="account-avatar-img" />
                                ) : (
                                    <div className="account-avatar-fallback">
                                        <User size={36} style={{ color: '#FFFFFF' }} />
                                    </div>
                                )}
                                <label className="account-avatar-upload-badge" title={isRtl ? 'تغيير الصورة الشخصية' : 'Change Profile Picture'}>
                                    <Camera size={14} />
                                    <input 
                                        type="file" 
                                        accept="image/*" 
                                        onChange={handleImageChange} 
                                        disabled={submitting} 
                                        style={{ display: 'none' }} 
                                    />
                                </label>
                            </div>
                        </div>

                        <div className="account-identity-info">
                            <div className="account-name-badge-row">
                                <h1 className="account-display-name">
                                    {fullName || user?.email?.split('@')[0] || 'VIP Member'}
                                </h1>
                                <span className="account-vip-badge">
                                    <Crown size={13} style={{ color: '#FFFFFF' }} />
                                    <span>VIP PLATINUM</span>
                                </span>
                            </div>
                            <p className="account-email-text">
                                <Mail size={13} />
                                <span>{user?.email || 'customer@luxvono.com'}</span>
                            </p>

                            {user?.customerId && (
                                <div className="account-customer-id-pill" onClick={handleCopyId} title={isRtl ? 'انقر لنسخ المعرّف' : 'Click to copy Customer ID'}>
                                    <span className="id-label">{t('CUSTOMER_ID') || 'معرّف العميل'}:</span>
                                    <strong className="id-value">{user.customerId}</strong>
                                    <button type="button" className="id-copy-btn">
                                        {copied ? <Check size={13} style={{ color: '#10b981' }} /> : <Copy size={13} />}
                                        <span>{copied ? (isRtl ? 'تم النسخ' : 'Copied') : (isRtl ? 'نسخ' : 'Copy')}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="account-header-kpis">
                        <div className="account-kpi-item">
                            <span className="kpi-label">{isRtl ? 'نقاط الولاء الفاخرة' : 'Luxury Reward Points'}</span>
                            <strong className="kpi-value">3,400 pts</strong>
                        </div>
                        <div className="account-kpi-item">
                            <span className="kpi-label">{isRtl ? 'الضمان والأصالة' : 'Authentication Status'}</span>
                            <span className="kpi-verified-badge">
                                <Shield size={12} /> {isRtl ? 'موثق 100%' : '100% Certified'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Notifications & Alerts */}
                {successMsg && (
                    <div className="luxury-alert-box luxury-alert--success">
                        <CheckCircle2 size={18} />
                        <span>{successMsg}</span>
                    </div>
                )}
                {error && (
                    <div className="luxury-alert-box luxury-alert--error">
                        <AlertCircle size={18} />
                        <span style={{ whiteSpace: 'pre-line' }}>{error}</span>
                    </div>
                )}

                {/* Main 2-Column Responsive Grid */}
                <div className="luxury-account-grid">
                    {/* Left Column: Navigation & Perks Card */}
                    <aside className="luxury-account-sidebar">
                        <div className="luxury-account-card">
                            <h3 className="sidebar-card-title">
                                <Sparkles size={16} style={{ color: '#FFFFFF' }} />
                                <span>{isRtl ? 'لوحة التحكم والخدمات' : 'Control Center'}</span>
                            </h3>

                            <nav className="account-sidebar-nav">
                                <button type="button" className="account-nav-btn active">
                                    <User size={16} />
                                    <span>{isRtl ? 'بيانات الحساب والشحن' : 'Profile & Logistics'}</span>
                                </button>
                                <Link to="/orders" className="account-nav-btn">
                                    <Package size={16} />
                                    <span>{isRtl ? 'سجل الطلبات والمشتريات' : 'Orders & Shipments'}</span>
                                </Link>
                                <Link to="/sourcing" className="account-nav-btn">
                                    <Briefcase size={16} />
                                    <span>{isRtl ? 'طلبات الاستيراد الخاص (RFQ)' : 'Bespoke Sourcing (RFQ)'}</span>
                                </Link>
                                <Link to="/vip" className="account-nav-btn">
                                    <Crown size={16} />
                                    <span>{isRtl ? 'خدمات كونسيرج صالون VIP' : 'VIP Concierge Salon'}</span>
                                </Link>
                            </nav>

                            <div className="account-sidebar-divider" />

                            <div className="account-security-card">
                                <div className="security-card-head">
                                    <Shield size={15} style={{ color: '#34d399' }} />
                                    <h4>{isRtl ? 'حماية الحساب والأمان' : 'Account Security'}</h4>
                                </div>
                                <p>{isRtl ? 'حسابك محمي بالتشفير السيادي والتوثيق المزدوج.' : 'Your account is secured with end-to-end encryption.'}</p>
                                <Link to="/forgot-password" className="security-link">
                                    {isRtl ? 'إعادة ضبط كلمة المرور →' : 'Reset Password →'}
                                </Link>
                            </div>

                            <button type="button" onClick={logout} className="account-sidebar-logout-btn">
                                <LogOut size={15} />
                                <span>{t('LOGOUT') || 'تسجيل الخروج'}</span>
                            </button>
                        </div>
                    </aside>

                    {/* Right Column: Personal & Shipping Logistics Form */}
                    <main className="luxury-account-main-content">
                        <div className="luxury-account-card">
                            <div className="card-heading-box">
                                <div>
                                    <h2 className="card-main-title">
                                        {isRtl ? 'بيانات الملف الشخصي وعنوان التوصيل' : 'Personal & Shipping Logistics'}
                                    </h2>
                                    <p className="card-subtitle">
                                        {isRtl 
                                            ? 'تحكم في معلوماتك الشخصية، رقم هاتفك لتأكيد الشحنات، وعنوان التسليم الافتراضي لتسريع عمليات الشراء والشحن الدولي.'
                                            : 'Manage your verified personal identity, contact phone number, and default delivery address for rapid global shipping.'}
                                    </p>
                                </div>
                            </div>

                            <form onSubmit={handleSubmit} className="luxury-profile-form">
                                <div className="luxury-form-row">
                                    <div className="luxury-form-group">
                                        <label htmlFor="fullName">
                                            <User size={14} />
                                            <span>{t('FULL_NAME') || 'الاسم الكامل'} *</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="fullName"
                                            value={fullName}
                                            onChange={(e) => setFullName(e.target.value)}
                                            placeholder={isRtl ? 'مثال: محمد العمري' : 'e.g. John Doe'}
                                            className="luxury-text-input"
                                            required
                                        />
                                    </div>

                                    <div className="luxury-form-group">
                                        <label htmlFor="phoneNumber">
                                            <Phone size={14} />
                                            <span>{t('PHONE_NUMBER_OPTIONAL') || 'رقم الهاتف للتواصل والشحن'}</span>
                                        </label>
                                        <input
                                            type="tel"
                                            id="phoneNumber"
                                            value={phoneNumber}
                                            onChange={(e) => setPhoneNumber(e.target.value)}
                                            placeholder="+966 5X XXX XXXX"
                                            className="luxury-text-input"
                                            dir="ltr"
                                        />
                                    </div>
                                </div>

                                <div className="luxury-form-group full-width">
                                    <label htmlFor="address">
                                        <MapPin size={14} />
                                        <span>{t('ADDRESS') || 'العنوان المعتمد (الشارع، الحي، المبنى)'} *</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="address"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        placeholder={isRtl ? 'مثال: طريق الملك فهد، حي العليا، برج النخيل، طابق 14' : 'e.g. 742 Evergreen Terrace, Penthouse Suite 4B'}
                                        className="luxury-text-input"
                                        required
                                    />
                                </div>

                                <div className="luxury-form-row">
                                    <div className="luxury-form-group">
                                        <label htmlFor="city">
                                            <span>{t('CITY') || 'المدينة'} *</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="city"
                                            value={city}
                                            onChange={(e) => setCity(e.target.value)}
                                            placeholder={isRtl ? 'الرياض / دبي / لندن...' : 'Riyadh / Dubai / London...'}
                                            className="luxury-text-input"
                                            required
                                        />
                                    </div>

                                    <div className="luxury-form-group">
                                        <label htmlFor="country">
                                            <span>{t('COUNTRY') || 'الدولة'} *</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="country"
                                            value={country}
                                            onChange={(e) => setCountry(e.target.value)}
                                            placeholder={isRtl ? 'المملكة العربية السعودية / الإمارات...' : 'Saudi Arabia / UAE / USA...'}
                                            className="luxury-text-input"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="luxury-form-actions">
                                    <button 
                                        type="submit" 
                                        className="luxury-submit-btn" 
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-icon" />
                                                <span>{t('SAVING_PROFILE') || 'جاري حفظ البيانات...'}</span>
                                            </>
                                        ) : (
                                            <>
                                                <Save size={16} />
                                                <span>{t('SAVE_PROFILE') || 'حفظ بيانات الملف الشخصي'}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* Exclusive VIP Vouchers & Offers Card */}
                        <div className="luxury-account-card" style={{ marginTop: 24 }}>
                            <div className="card-heading-box" style={{ marginBottom: 18 }}>
                                <div>
                                    <h2 className="card-main-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Ticket size={18} style={{ color: '#FFFFFF' }} />
                                        <span>{isRtl ? 'قسائمي وعروضي الحصرية' : 'My Exclusive VIP Vouchers & Perks'}</span>
                                    </h2>
                                    <p className="card-sub-title">
                                        {isRtl ? 'أكواد الخصم والعروض الخاصة المخصصة لحسابك بموجب عضويتك البلاتينية' : 'Personalized promo codes and private discounts attached to your VIP profile'}
                                    </p>
                                </div>
                            </div>

                            {userEligibleCoupons.length === 0 ? (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '36px 16px',
                                    background: 'rgba(255, 255, 255, 0.02)',
                                    borderRadius: 14,
                                    border: '1px dashed rgba(255, 255, 255, 0.25)',
                                    color: '#94a3b8'
                                }}>
                                    <Ticket size={36} style={{ color: '#FFFFFF', marginBottom: 12, opacity: 0.7 }} />
                                    <p style={{ margin: '0 0 6px', color: '#f8fafc', fontWeight: 700, fontSize: '0.95rem' }}>
                                        {isRtl ? 'لا توجد قسائم نشطة مرتبطة بحسابك حالياً' : 'No active vouchers linked to your account at this time'}
                                    </p>
                                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#94a3b8' }}>
                                        {isRtl ? 'تابع العروض الموسمية وإشعارات المتجر للحصول على أكواد الخصم الترويجية الحصرية المعتمدة.' : 'Stay tuned for seasonal promotions and verified concierge vouchers.'}
                                    </p>
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
                                    {userEligibleCoupons.map((coupon) => {
                                        const isCodeCopied = voucherCopied === coupon.code;
                                        return (
                                            <div
                                                key={coupon.id}
                                                style={{
                                                    background: 'linear-gradient(135deg, rgba(18, 16, 14, 0.95) 0%, rgba(43, 31, 13, 0.8) 100%)',
                                                    border: '1.5px solid rgba(255, 255, 255, 0.35)',
                                                    borderRadius: 14,
                                                    padding: 16,
                                                    position: 'relative',
                                                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                                    <span style={{ fontSize: '1.4rem', fontWeight: 900, color: '#FFFFFF', fontFamily: 'Inter, sans-serif' }}>
                                                        {coupon.amount} OFF
                                                    </span>
                                                    <span style={{
                                                        fontSize: '0.7rem',
                                                        background: 'rgba(255, 255, 255, 0.15)',
                                                        border: '1px solid #FFFFFF',
                                                        color: '#FFFFFF',
                                                        padding: '2px 8px',
                                                        borderRadius: 12,
                                                        fontWeight: 700
                                                    }}>
                                                        {coupon.badge}
                                                    </span>
                                                </div>

                                                <h4 style={{ margin: '0 0 4px', color: '#f8fafc', fontSize: '0.95rem', fontWeight: 700 }}>
                                                    {isRtl ? coupon.titleAr : coupon.titleEn}
                                                </h4>
                                                <p style={{ margin: '0 0 14px', color: '#94a3b8', fontSize: '0.78rem', lineHeight: 1.4 }}>
                                                    {isRtl ? coupon.descAr : coupon.descEn}
                                                </p>

                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    background: 'rgba(0,0,0,0.5)',
                                                    border: '1px dashed #FFFFFF',
                                                    borderRadius: 8,
                                                    padding: '6px 10px'
                                                }}>
                                                    <strong style={{ color: '#FFFFFF', letterSpacing: '1px', fontSize: '0.95rem' }}>
                                                        {coupon.code}
                                                    </strong>
                                                    <div style={{ display: 'flex', gap: 6 }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(coupon.code);
                                                                setVoucherCopied(coupon.code);
                                                                setTimeout(() => setVoucherCopied(null), 2000);
                                                            }}
                                                            style={{
                                                                background: isCodeCopied ? '#10b981' : 'rgba(255,255,255,0.08)',
                                                                color: isCodeCopied ? '#fff' : '#cbd5e1',
                                                                border: 'none',
                                                                borderRadius: 6,
                                                                padding: '4px 8px',
                                                                fontSize: '0.72rem',
                                                                cursor: 'pointer',
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {isCodeCopied ? (isRtl ? 'تم النسخ ✓' : 'Copied ✓') : (isRtl ? 'نسخ' : 'Copy')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const couponObj = {
                                                                    code: coupon.code,
                                                                    title: coupon.titleAr || coupon.titleEn || coupon.code,
                                                                    discountType: coupon.discountType,
                                                                    discountValue: coupon.discountValue,
                                                                    discountAmount: 0,
                                                                    minOrderAmount: coupon.minOrderAmount
                                                                };
                                                                localStorage.setItem('luxvono_active_coupon', JSON.stringify(couponObj));
                                                                navigate('/cart');
                                                            }}
                                                            style={{
                                                                background: 'linear-gradient(135deg, #FFFFFF 0%, #E2E8F0 100%)',
                                                                color: '#000',
                                                                border: 'none',
                                                                borderRadius: 6,
                                                                padding: '4px 10px',
                                                                fontSize: '0.72rem',
                                                                cursor: 'pointer',
                                                                fontWeight: 700
                                                            }}
                                                        >
                                                            {isRtl ? 'تطبيق بالسلة 🛍️' : 'Apply in Cart 🛍️'}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </main>
                </div>
            </div>
        </div>
    );
};

export default ProfileCompletionPage;
